import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./Business_hours.module.css";
import {
  fetchLlmDetails,
  getBusinessDetailsByBusinessId,
  getBusinessHoursByBusinessId,
  updateBusinessHoursByBusinessId,
  updateLlm,
} from "../../Store/apiStore";
import { getAgentEngineId } from "../../utils/agentEngineId";

const DAYS = [
  { key: "Monday", short: "Mon" },
  { key: "Tuesday", short: "Tue" },
  { key: "Wednesday", short: "Wed" },
  { key: "Thursday", short: "Thu" },
  { key: "Friday", short: "Fri" },
  { key: "Saturday", short: "Sat" },
  { key: "Sunday", short: "Sun" },
];

const DEFAULT_TIME = { open: "00:00", close: "23:59", closed: false };

function parseHoursString(hoursRaw = "") {
  const map = {};
  const noHours =
    !hoursRaw ||
    typeof hoursRaw !== "string" ||
    hoursRaw.trim().length === 0;

  DAYS.forEach((d) => {
    map[d.key] = noHours
      ? { open: "00:00", close: "23:59", closed: false }
      : { ...DEFAULT_TIME };
  });

  if (noHours) return map;

  const blocks = hoursRaw.split("|").map((s) => s.trim()).filter(Boolean);

  for (const block of blocks) {
    const idx = block.indexOf(":");
    if (idx < 0) continue;

    const day = block.slice(0, idx).trim();
    const timePart = block.slice(idx + 1).trim();

    if (!map[day]) continue;

    if (/closed/i.test(timePart)) {
      map[day] = { open: "", close: "", closed: true };
      continue;
    }

    const [openStr, closeStr] = timePart.split(/–|-/).map((s) => s.trim());

    map[day] = {
      open: to24h(openStr || "09:00 AM"),
      close: to24h(closeStr || "06:00 PM"),
      closed: false,
    };
  }

  return map;
}

function buildHoursString(map) {
  return DAYS.map(({ key }) => {
    const v = map[key] || DEFAULT_TIME;
    if (v.closed) return `${key}: Closed`;

    const open = to12h(v.open || "09:00");
    const close = to12h(v.close || "18:00");
    return `${key}: ${open} – ${close}`;
  }).join(" | ");
}

function to24h(str) {
  const s = String(str).replace(/\u202f/g, " ").replace(/\s+/g, " ").trim();
  if (/^\d{1,2}:\d{2}$/.test(s)) {
    const [h, m] = s.split(":").map(Number);
    return `${String(Math.min(Math.max(h, 0), 23)).padStart(2, "0")}:${String(
      Math.min(Math.max(m, 0), 59)
    ).padStart(2, "0")}`;
  }

  const match = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (!match) return "09:00";

  let h = parseInt(match[1], 10);
  const m = parseInt(match[2] || "00", 10);
  const ap = match[3].toUpperCase();

  if (ap === "PM" && h !== 12) h += 12;
  if (ap === "AM" && h === 12) h = 0;

  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function to12h(hhmm) {
  const [H, M] = String(hhmm || "09:00").split(":");
  let h = parseInt(H || "9", 10);
  const m = parseInt(M || "0", 10);

  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;

  return `${h}:${String(m).padStart(2, "0")} ${ap}`;
}

const Business_hours = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const handleBackClick = () => navigate(-1);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [hoursMap, setHoursMap] = useState(() => parseHoursString(""));
  const hoursPreview = useMemo(() => buildHoursString(hoursMap), [hoursMap]);

  useEffect(() => {
    if (!businessId) {
      setLoading(false);
      setError("BusinessId missing in URL");
      return;
    }

    (async () => {
      try {
        setLoading(true);
        setError("");
        setSuccess("");

        const data = await getBusinessHoursByBusinessId(businessId);
        const parsed = parseHoursString(data?.hoursRaw || "");
        setHoursMap(parsed);
      } catch (e) {
        setError(e?.response?.data?.error || e.message || "Failed to fetch hours");
      } finally {
        setLoading(false);
      }
    })();
  }, [businessId]);

  const handleToggleClosed = (dayKey) => {
    setHoursMap((prev) => {
      const curr = prev[dayKey] || DEFAULT_TIME;
      const nextClosed = !curr.closed;
      return {
        ...prev,
        [dayKey]: nextClosed
          ? { open: "", close: "", closed: true }
          : { open: "09:00", close: "18:00", closed: false },
      };
    });
  };

  const handleTimeChange = (dayKey, field, value) => {
    setHoursMap((prev) => ({
      ...prev,
      [dayKey]: { ...(prev[dayKey] || DEFAULT_TIME), [field]: value, closed: false },
    }));
  };

const agentData = JSON.parse(sessionStorage.getItem("agentDetails") || "{}");
// Conversation flow agent ka llmId nahi hota — tab agent_id (getAgentEngineId).
const lId =
  getAgentEngineId(agentData?.agent) ||
  getAgentEngineId(agentData) ||
  "";


const handleSave = async () => {
  try {
    if (!businessId) return setError("BusinessId missing in URL");
    setSaving(true);
    setError("");
    setSuccess("");
    // 1) Save in DB
    await updateBusinessHoursByBusinessId(businessId, hoursPreview);
    // 2) Try to get llmId from business details
    let biz = null;
    try {
      const bizRes = await getBusinessDetailsByBusinessId(businessId);
      // console.log("[BH] getBusinessDetailsByBusinessId resp ✅", bizRes);
      biz = bizRes?.data ?? bizRes ?? null;
    } catch (err) {
      console.warn(
        err?.response?.data || err?.message || err
      );
    }
    // 3) Derive llmId with fallback
    const llmId =
      biz?.llmId ||
      biz?.llm_id ||
      biz?.agent?.llmId ||
      biz?.agent?.llm_id ||
      biz?.business?.llmId ||
      biz?.business?.llm_id ||
      lId ||
      getAgentEngineId(biz?.agent) ||
      "";
    if (!llmId) {
      setSuccess("Business hours updated ✅ (LLM not linked yet)");
      setTimeout(() => setSuccess(""), 2500);
      return;
    }
    const token = localStorage.getItem("token") || "";
    if (!token) {
      setSuccess("Business hours updated ✅ (Login token missing, LLM not synced)");
      setTimeout(() => setSuccess(""), 2500);
      return;
    }
    // 4) Fetch current LLM config
    const llmRes = await fetchLlmDetails(llmId);
    const currentConfig = llmRes?.data?.data || llmRes?.data?.data?.data;
    if (!currentConfig) {
      console.warn("[BH] currentConfig missing -> cannot update LLM vars");
      setSuccess("Business hours updated ✅ (LLM config not found)");
      setTimeout(() => setSuccess(""), 2500);
      return;
    }
    // 5) Merge vars
    const mergedVars = {
      ...(currentConfig.default_dynamic_variables || {}),
      business_hours: hoursPreview, 
    };
    // 6) Update LLM
    await updateLlm(token, llmId, {
      ...currentConfig,
      default_dynamic_variables: mergedVars,
    });
    setSuccess("Business hours updated & synced ✅");
    setTimeout(() => setSuccess(""), 2500);
  } catch (e) {
    console.error(
      "[BH] handleSave failed ❌",
      e?.response?.data || e?.message || e
    );
    setError(e?.response?.data?.error || e?.message || "Failed to save");
  } finally {
    setSaving(false);
  }
};



  return (
    <div className={styles.page}>
      {/* ✅ TOP HEADER */}
      <header className={styles.topHeader}>
        <div className={styles.profileBack} onClick={handleBackClick}>
          <img src="/svg/Notification.svg" alt="Back" />
          <div className={styles.profileSection}>
            <p className={styles.name}>Business Hours</p>
          </div>
        </div>

        <div className={styles.notifiMain}>
          <button
            className={styles.saveBtnHeader}
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </header>

      {/* ✅ BODY */}
      <div className={styles.wrap}>
        {error ? <div className={styles.alertError}>{error}</div> : null}
        {success ? <div className={styles.alertSuccess}>{success}</div> : null}

        <div className={styles.card}>
          {loading ? (
            <div className={styles.skeleton}>
              <div className={styles.skelLine} />
              <div className={styles.skelLine} />
              <div className={styles.skelLine} />
            </div>
          ) : (
            <>
              <div className={styles.tableHead}>
                <span>Day</span>
                <span>Open</span>
                <span>Close</span>
                <span>Status</span>
              </div>

              <div className={styles.rows}>
                {DAYS.map((d) => {
                  const v = hoursMap[d.key] || DEFAULT_TIME;
                  return (
                    <div className={styles.row} key={d.key}>
                      <div className={styles.dayCell}>
                        <span className={styles.dayPill}>{d.short}</span>
                        <span className={styles.dayName}>{d.key}</span>
                      </div>

                      <div className={styles.timeCell}>
                        <input
                          type="time"
                          value={v.closed ? "" : v.open}
                          disabled={v.closed}
                          onChange={(e) =>
                            handleTimeChange(d.key, "open", e.target.value)
                          }
                          className={styles.timeInput}
                        />
                      </div>

                      <div className={styles.timeCell}>
                        <input
                          type="time"
                          value={v.closed ? "" : v.close}
                          disabled={v.closed}
                          onChange={(e) =>
                            handleTimeChange(d.key, "close", e.target.value)
                          }
                          className={styles.timeInput}
                        />
                      </div>

                      <div className={styles.statusCell}>
                        <button
                          type="button"
                          className={`${styles.toggleBtn} ${v.closed ? styles.toggleOff : styles.toggleOn
                            }`}
                          onClick={() => handleToggleClosed(d.key)}
                        >
                          <span className={styles.toggleDot} />
                          {v.closed ? "Closed" : "Open"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>

  );
};

export default Business_hours;
